import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@mui/material';
import FullCalendar from '@fullcalendar/react';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  createPblAppointmentProposal,
  updatePblAppointmentProposal,
  createPblMeeting,
  getPblCalendarEvents,
  getPblCase,
  getPblNonBusinessDates,
} from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import { AppointmentProposalEditModal } from '@/components/Pbl/CaseProgressDetail/Modals';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { useAppSelector } from '@/redux/store.hook';
import { isAssignedPblCaseLawyer } from '../CaseProgressDetail/caseProgressState';
import { Button } from '@/components/UI/Button';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblAppointmentOption, PblAppointmentProposal, PblAppointmentProposalUpdatePayload, PblCalendarEvent, PblCase, PblMeeting } from '@/types/pbl';
import { formatPblEnglishChineseName } from '@/utils/pblDisplay';
import { toHongKongApiDateTime, toHongKongCalendarDateTime } from '@/utils/pblDate';
import { getPblMeetingEndDateTime, isPblMeetingWithinSameDay } from '@/utils/pblMeeting';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import { InfoGrid, InfoLabel, InfoValue, SectionTitle, TitleBar } from '../OcManageCases/styles';
import {
  CalendarPanel,
  CaseDeadlineNotice,
  CalendarScroller,
  HiddenLabelText,
  MeetingActionButton,
  MeetingDescriptionInput,
  MeetingEndOutput,
  MeetingFieldRow,
  MeetingFieldStack,
  MeetingInput,
  MeetingInputWrap,
  MeetingModalAction,
  MeetingModalCloseButton,
  MeetingModalSurface,
  MeetingModalTitle,
  MeetingOptionActionRow,
  MeetingOptionHeader,
  MeetingOptionPanel,
  MeetingSecondaryButton,
  MeetingPageShell,
  MeetingSuccessText,
  MeetingSuccessTitle,
  MeetingValidationText,
  SummaryPanel,
} from './styles';
import { useTranslation } from 'react-i18next';

type CalendarEvent = {
  id: string;
  title: string;
  start: string;
  end?: string;
  color: string;
};

type MeetingForm = {
  description: string;
  options: MeetingOptionForm[];
};

type MeetingOptionForm = {
  date: string;
  startTime: string;
};

const MINIMUM_MEETING_LEAD_TIME_MINUTES = 60;
const DEFAULT_MEETING_DESCRIPTION = '';
const MINIMUM_APPOINTMENT_OPTIONS = 2;
const MAXIMUM_APPOINTMENT_OPTIONS = 5;
const MINIMUM_MEETING_TIME_MESSAGE = 'Meeting time must be at least 1 hour from now.';
const CALENDAR_HISTORY_LOOKBACK_YEARS = 1;
const CALENDAR_HISTORY_LOOKAHEAD_YEARS = 1;
const CALENDAR_EVENT_COLORS = {
  confirmed: '#2E7D32',
  pending: '#FF6D00',
} as const;

const pendingCalendarCaseStatuses = new Set(['pending_lawyer_timeslot', 'pending_appointment_confirmation']);
const pendingCalendarStatuses = new Set(['pending', 'pending_confirmation', 'pending_citizen_selection']);
const hiddenCalendarStatuses = new Set(['cancelled', 'canceled', 'declined', 'rejected']);

const padTwoDigits = (value: number) => String(value).padStart(2, '0');

const formatDateForInput = (date: Date) =>
  `${date.getFullYear()}-${padTwoDigits(date.getMonth() + 1)}-${padTwoDigits(date.getDate())}`;

const formatTimeForInput = (date: Date) => `${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}`;

const formatDateTimeForApi = toHongKongApiDateTime;

const getOptionalString = (event: PblCalendarEvent, key: string) => {
  const value = (event as unknown as Record<string, unknown>)[key];

  return typeof value === 'string' ? value.trim().toLowerCase() : '';
};

const getOptionalBoolean = (event: PblCalendarEvent, key: string) => {
  const value = (event as unknown as Record<string, unknown>)[key];

  return typeof value === 'boolean' ? value : undefined;
};

const isPendingCalendarEvent = (event: PblCalendarEvent) => {
  const explicitConfirmed = getOptionalBoolean(event, 'is_confirmed') ?? getOptionalBoolean(event, 'confirmed');
  const eventStatus =
    getOptionalString(event, 'status') ||
    getOptionalString(event, 'event_status') ||
    getOptionalString(event, 'meeting_status') ||
    getOptionalString(event, 'confirmation_status');
  const eventType = String(event.type || '').toLowerCase();

  if (explicitConfirmed === false) return true;
  if (explicitConfirmed === true) return false;

  return (
    pendingCalendarStatuses.has(eventStatus) ||
    pendingCalendarCaseStatuses.has(event.case_status) ||
    eventType.includes('appointment_proposal') ||
    eventType.includes('timeslot')
  );
};

const shouldDisplayCalendarEvent = (event: PblCalendarEvent) => {
  const eventStatus =
    getOptionalString(event, 'status') ||
    getOptionalString(event, 'event_status') ||
    getOptionalString(event, 'meeting_status') ||
    getOptionalString(event, 'confirmation_status');

  return !hiddenCalendarStatuses.has(eventStatus);
};

const getCalendarEventColor = (event: PblCalendarEvent) =>
  isPendingCalendarEvent(event) ? CALENDAR_EVENT_COLORS.pending : CALENDAR_EVENT_COLORS.confirmed;

const formatCalendarLabelTime = (value: string) => {
  const timeMatch = value.match(/[T ](\d{2}):(\d{2})/);
  let hours = Number(timeMatch?.[1]);
  let minutes = timeMatch?.[2] || '00';

  if (!Number.isFinite(hours)) {
    const date = new Date(value);
    hours = date.getHours();
    minutes = String(date.getMinutes()).padStart(2, '0');
  }

  const period = hours >= 12 ? 'pm' : 'am';
  const displayHour = hours % 12 || 12;

  return `${String(displayHour).padStart(2, '0')}:${minutes}${period}`;
};

const getCalendarLabelCaseId = (event: PblCalendarEvent) => event.case_id || String(event.case_internal_id);

const getCalendarEventTitle = (event: PblCalendarEvent) =>
  `${formatCalendarLabelTime(event.start)} ${getCalendarLabelCaseId(event)}`;

const getCalendarEventId = (event: PblCalendarEvent) => {
  const eventType = String(event.type || '').toLowerCase();

  if (eventType === 'pbl_meeting' && event.meeting_id) return `meeting-${event.meeting_id}`;
  if (event.proposal_id != null && event.option_index != null) {
    return `proposal-${event.proposal_id}-${event.option_index}`;
  }

  return `calendar-${event.id}`;
};

const mapPblEventToCalendarEvent = (event: PblCalendarEvent): CalendarEvent => ({
  id: getCalendarEventId(event),
  title: getCalendarEventTitle(event),
  start: toHongKongCalendarDateTime(event.start) || event.start,
  end: toHongKongCalendarDateTime(event.end),
  color: getCalendarEventColor(event),
});

const parseInputDate = (date: string) => {
  const normalizedDate = date.replace(/\//g, '-');
  const [year, month, day] = normalizedDate.split('-').map(Number);

  if (!year || !month || !day) return null;

  const parsedDate = new Date(year, month - 1, day);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const parseInputTime = (time: string) => {
  const [hour, minute] = time.split(':').map(Number);

  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

  return { hour, minute };
};

const buildLocalDateTime = (date: string, time: string) => {
  const parsedDate = parseInputDate(date);
  const parsedTime = parseInputTime(time);

  if (!parsedDate || !parsedTime) return null;

  return new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
    parsedTime.hour,
    parsedTime.minute,
    0,
    0
  );
};

const addDays = (date: Date, days: number) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);

  return nextDate;
};

const addYears = (date: Date, years: number) => {
  const nextDate = new Date(date);
  nextDate.setFullYear(nextDate.getFullYear() + years);

  return nextDate;
};

const getMinimumMeetingDateTime = () => {
  const minimumDateTime = new Date();
  minimumDateTime.setMinutes(minimumDateTime.getMinutes() + MINIMUM_MEETING_LEAD_TIME_MINUTES);
  minimumDateTime.setSeconds(0, 0);

  const remainingMinutes = minimumDateTime.getMinutes() % 5;
  if (remainingMinutes > 0) {
    minimumDateTime.setMinutes(minimumDateTime.getMinutes() + (5 - remainingMinutes));
  }

  return minimumDateTime;
};

const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const isSameInputDate = (date: Date, inputDate: string) => formatDateForInput(date) === inputDate;

const isDateBeforeMinimumDay = (date: Date, minimumDateTime: Date) =>
  getStartOfDay(date) < getStartOfDay(minimumDateTime);

const buildMeetingOptionFromStart = (startDateTime: Date): MeetingOptionForm => {
  return {
    date: formatDateForInput(startDateTime),
    startTime: formatTimeForInput(startDateTime),
  };
};

const getDefaultMeetingOption = (date: Date, minimumDateTime = getMinimumMeetingDateTime()): MeetingOptionForm => {
  const selectedDefaultStart = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0, 0, 0);
  const startDateTime =
    isSameInputDate(minimumDateTime, formatDateForInput(date)) && selectedDefaultStart < minimumDateTime
      ? minimumDateTime
      : selectedDefaultStart;

  return buildMeetingOptionFromStart(startDateTime);
};

const getDefaultMeetingValues = (
  date: Date,
  minimumDateTime = getMinimumMeetingDateTime(),
  shouldCreateAppointmentProposal = false
): MeetingForm => {
  const firstOption = getDefaultMeetingOption(date, minimumDateTime);
  const options = [firstOption];

  if (shouldCreateAppointmentProposal) {
    const firstStartDateTime = buildLocalDateTime(firstOption.date, firstOption.startTime) || date;
    options.push(getDefaultMeetingOption(addDays(firstStartDateTime, 1), minimumDateTime));
  }

  return {
    description: DEFAULT_MEETING_DESCRIPTION,
    options,
  };
};

const getMetadataText = (metadata: PblMeeting['metadata'], key: string) => {
  const value = metadata?.[key];

  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
};

const normalizeCalendarDateTime = toHongKongCalendarDateTime;

const mapMeetingToCalendarEvent = (meeting: PblMeeting): CalendarEvent => ({
  id: `meeting-${meeting.id}`,
  title: meeting.display_title || 'Pro Bono Legal Consultation',
  start: normalizeCalendarDateTime(meeting.scheduled_at) || normalizeCalendarDateTime(meeting.scheduled_at_display) || meeting.scheduled_at,
  end:
    normalizeCalendarDateTime(meeting.ends_at) ||
    normalizeCalendarDateTime(meeting.ends_at_display) ||
    normalizeCalendarDateTime(getMetadataText(meeting.metadata, 'end_at')) ||
    normalizeCalendarDateTime(getMetadataText(meeting.metadata, 'end_at_display')),
  color: '#0B319D',
});

const getPblCaseDisplayId = (item: PblCase) => item.case_id || item.case_reference || String(item.id);

const buildCaseDetailPath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const getCaseSummary = (item: PblCase | null, isLoading: boolean) => ({
  officeName: item?.oc_office_name || (isLoading ? 'Loading...' : '-'),
  assistingStaffName: item?.assisting_staff_name || (isLoading ? 'Loading...' : '-'),
  officeContact: item?.office_contact || (isLoading ? 'Loading...' : '-'),
  caseId: item ? getPblCaseDisplayId(item) : isLoading ? 'Loading...' : '-',
  enquirer: item
    ? formatPblEnglishChineseName(item.citizen_full_name_en, item.citizen_full_name_zh, item.citizen_full_name)
    : isLoading
    ? 'Loading...'
    : '-',
  identityPartial: item?.identity_partial || (isLoading ? 'Loading...' : '-'),
});

const PblLawyerMeetingCalendar = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userData } = useAppSelector(getAuthSelector);
  const { caseId } = useParams();
  const { isMobile, isTablet } = useResize();
  const minimumMeetingDateTime = useMemo(() => getMinimumMeetingDateTime(), []);
  const calendarHistoryFromDateInput = useMemo(
    () => formatDateForInput(addYears(new Date(), -CALENDAR_HISTORY_LOOKBACK_YEARS)),
    []
  );
  const calendarHistoryToDateInput = useMemo(
    () => formatDateForInput(addYears(new Date(), CALENDAR_HISTORY_LOOKAHEAD_YEARS)),
    []
  );
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState<PblAppointmentProposal | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [upcomingCalendarEvents, setUpcomingCalendarEvents] = useState<CalendarEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState(formatDateForInput(minimumMeetingDateTime));
  const [isCreatingMeeting, setIsCreatingMeeting] = useState(false);
  const [isLoadingCase, setIsLoadingCase] = useState(false);
  const [pblCase, setPblCase] = useState<PblCase | null>(null);
  const [hasSubmittedMeetingRequest, setHasSubmittedMeetingRequest] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [nonBusinessDates, setNonBusinessDates] = useState<Set<string> | null>(null);

  const {
    clearErrors,
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
  } = useForm<MeetingForm>({
    defaultValues: getDefaultMeetingValues(minimumMeetingDateTime, minimumMeetingDateTime),
  });

  const watchedOptions = watch('options') || [];
  const currentMinimumMeetingDateTime = getMinimumMeetingDateTime();
  const minimumDateInput = formatDateForInput(currentMinimumMeetingDateTime);
  const getMinimumTimeInputForDate = (date?: string) =>
    date && isSameInputDate(currentMinimumMeetingDateTime, date)
      ? formatTimeForInput(currentMinimumMeetingDateTime)
      : undefined;
  const caseSummary = getCaseSummary(pblCase, isLoadingCase);
  const latestProposal = pblCase?.appointment_proposals?.reduce<PblAppointmentProposal | null>(
    (latest, proposal) => !latest || proposal.id > latest.id ? proposal : latest,
    null
  );
  const shouldCreateAppointmentProposal = pblCase?.status === 'pending_lawyer_timeslot';
  const editableProposal = pblCase?.status === 'pending_appointment_confirmation'
    && Number(latestProposal?.created_by_user_id) === Number(userData?.id)
    && isAssignedPblCaseLawyer(pblCase, userData, new Set(['accepted']))
    && latestProposal?.status === 'pending_citizen_selection' ? latestProposal : null;
  const displayedOptions = shouldCreateAppointmentProposal ? watchedOptions : watchedOptions.slice(0, 1);
  const calendarEvents = useMemo(() => {
    const nextEvents = new Map<string, CalendarEvent>();

    events.forEach((event) => nextEvents.set(event.id, event));
    upcomingCalendarEvents.forEach((event) => nextEvents.set(event.id, event));

    return Array.from(nextEvents.values());
  }, [events, upcomingCalendarEvents]);
  const isMeetingDateUnavailable = (date: Date) =>
    nonBusinessDates === null ||
    isDateBeforeMinimumDay(date, currentMinimumMeetingDateTime) ||
    nonBusinessDates.has(formatDateForInput(date));

  useEffect(() => {
    const numericCaseId = Number(caseId);

    if (!Number.isFinite(numericCaseId)) {
      errorHandler(t('pbl.pagesHardcoded.lawyerMeetingCalendar.invalidCaseId'));
      return;
    }

    let isMounted = true;
    setIsLoadingCase(true);

    getPblCase(numericCaseId)
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToLoadCaseDetail'));
          return;
        }

        const nextCase = response.data.data;
        setPblCase(nextCase);
        setEvents((nextCase.meetings || []).map(mapMeetingToCalendarEvent));
      })
      .catch((error: any) => {
        if (!isMounted) return;
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToLoadCaseDetail'));
      })
      .finally(() => {
        if (isMounted) setIsLoadingCase(false);
      });

    return () => {
      isMounted = false;
    };
  }, [caseId, t]);

  useEffect(() => {
    let isMounted = true;

    getPblCalendarEvents({ from: calendarHistoryFromDateInput, to: calendarHistoryToDateInput })
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.eventCalendar.failedToLoadPblCalendarEvents'));
          return;
        }

        setUpcomingCalendarEvents(
          (response.data.data || [])
            .filter(shouldDisplayCalendarEvent)
            .map(mapPblEventToCalendarEvent)
        );
      })
      .catch((error: any) => {
        if (!isMounted) return;
        errorHandler(
          error.response?.data?.message ||
            error.response?.data?.data ||
            t('pbl.pagesHardcoded.eventCalendar.failedToLoadPblCalendarEvents')
        );
      });

    return () => {
      isMounted = false;
    };
  }, [calendarHistoryFromDateInput, calendarHistoryToDateInput, t]);

  useEffect(() => {
    let isMounted = true;

    getPblNonBusinessDates({ from: minimumDateInput, to: calendarHistoryToDateInput })
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(
            response.data.message || t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToLoadBusinessDays')
          );
          return;
        }

        setNonBusinessDates(new Set(response.data.data.non_business_dates || []));
      })
      .catch((error: any) => {
        if (!isMounted) return;
        errorHandler(
          error.response?.data?.message ||
            error.response?.data?.data ||
            t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToLoadBusinessDays')
        );
      });

    return () => {
      isMounted = false;
    };
  }, [calendarHistoryToDateInput, minimumDateInput, t]);

  const openCreateMeetingModal = (date: Date) => {
    if (editableProposal) {
      setEditingProposal(editableProposal);
      return;
    }
    if (pblCase?.status === 'pending_appointment_confirmation' && latestProposal) return;
    const latestMinimumDateTime = getMinimumMeetingDateTime();

    if (
      nonBusinessDates === null ||
      isDateBeforeMinimumDay(date, latestMinimumDateTime) ||
      nonBusinessDates.has(formatDateForInput(date))
    ) {
      return;
    }

    const nextValues = getDefaultMeetingValues(date, latestMinimumDateTime, shouldCreateAppointmentProposal);
    setSelectedDate(formatDateForInput(date));
    reset(nextValues);
    clearErrors();
    setMeetingOpen(true);
  };

  const closeCreateMeetingModal = () => {
    setMeetingOpen(false);
    clearErrors();
  };

  const goBack = () => {
    if (caseId && (events.length > 0 || hasSubmittedMeetingRequest)) {
      navigate(buildCaseDetailPath(caseId));
      return;
    }

    navigate(PATH.pbl.lawyerCases);
  };

  const addAppointmentOption = () => {
    if (watchedOptions.length >= MAXIMUM_APPOINTMENT_OPTIONS) return;

    const lastOption = watchedOptions[watchedOptions.length - 1];
    const lastStartDateTime = lastOption ? buildLocalDateTime(lastOption.date, lastOption.startTime) : null;
    const nextOption = getDefaultMeetingOption(addDays(lastStartDateTime || getMinimumMeetingDateTime(), 1));

    setValue('options', [...watchedOptions, nextOption], { shouldDirty: true });
  };

  const removeAppointmentOption = (index: number) => {
    if (watchedOptions.length <= MINIMUM_APPOINTMENT_OPTIONS) return;

    setValue(
      'options',
      watchedOptions.filter((_, optionIndex) => optionIndex !== index),
      { shouldDirty: true }
    );
  };

  const validateMeetingOptionDateTime = (option: MeetingOptionForm, index: number) => {
    const scheduledDateTime = buildLocalDateTime(option.date, option.startTime);
    const startsAt = `${option.date}T${option.startTime}`;
    const endsAt = getPblMeetingEndDateTime(startsAt);

    if (!scheduledDateTime || !endsAt) {
      setError(`options.${index}.startTime` as any, {
        type: 'validate',
        message: t('pbl.pagesHardcoded.lawyerMeetingCalendar.pleaseEnterAValidMeetingDateAndTime'),
      });
      errorHandler(t('pbl.pagesHardcoded.lawyerMeetingCalendar.pleaseEnterAValidMeetingDateAndTime'));
      return null;
    }

    if (nonBusinessDates === null || nonBusinessDates.has(option.date)) {
      const message = t('pbl.pagesHardcoded.lawyerMeetingCalendar.meetingDateMustBeBusinessDay');
      setError(`options.${index}.date` as any, { type: 'validate', message });
      errorHandler(message);
      return null;
    }

    if (scheduledDateTime < getMinimumMeetingDateTime()) {
      setError(`options.${index}.startTime` as any, { type: 'validate', message: MINIMUM_MEETING_TIME_MESSAGE });
      errorHandler(MINIMUM_MEETING_TIME_MESSAGE);
      return null;
    }

    if (!isPblMeetingWithinSameDay(startsAt)) {
      const message = t('pbl.common.meetingEndsSameDay');
      setError(`options.${index}.startTime` as any, { type: 'validate', message });
      errorHandler(message);
      return null;
    }

    return endsAt;
  };

  const getAppointmentProposalNote = (data: MeetingForm) => {
    const note = data.description.trim();

    return note || undefined;
  };

  const buildAppointmentProposalOptions = (data: MeetingForm) => {
    if (data.options.length < MINIMUM_APPOINTMENT_OPTIONS || data.options.length > MAXIMUM_APPOINTMENT_OPTIONS) {
      errorHandler(t('pbl.pagesHardcoded.lawyerMeetingCalendar.pleaseProvide2To5MeetingOptions'));
      return null;
    }

    const note = getAppointmentProposalNote(data);
    const options: PblAppointmentOption[] = [];

    for (let index = 0; index < data.options.length; index += 1) {
      const option = data.options[index];
      const validDateTime = validateMeetingOptionDateTime(option, index);

      if (!validDateTime) return null;

      options.push({
        starts_at: formatDateTimeForApi(option.date, option.startTime),
        ends_at: formatDateTimeForApi(validDateTime.slice(0, 10), validDateTime.slice(11)),
        ...(note ? { note } : {}),
      });
    }

    return options;
  };

  const buildMeetingPayload = (data: MeetingForm) => {
    const option = data.options[0];

    if (!option) {
      errorHandler(t('pbl.pagesHardcoded.lawyerMeetingCalendar.pleaseProvideAMeetingTime'));
      return null;
    }

    const validDateTime = validateMeetingOptionDateTime(option, 0);

    if (!validDateTime) return null;

    const description = data.description.trim();

    return {
      meeting_type: 'video' as const,
      scheduled_at: formatDateTimeForApi(option.date, option.startTime),
      ends_at: formatDateTimeForApi(validDateTime.slice(0, 10), validDateTime.slice(11)),
      metadata: {
        ...(description ? { description } : {}),
      },
    };
  };

  const createMeeting = handleSubmit(async (data) => {
    if (isCreatingMeeting || hasSubmittedMeetingRequest) return;

    const numericCaseId = Number(caseId);

    if (!Number.isFinite(numericCaseId)) {
      errorHandler(t('pbl.pagesHardcoded.lawyerMeetingCalendar.invalidCaseId'));
      return;
    }

    if (!pblCase) {
      errorHandler(t('pbl.pagesHardcoded.lawyerMeetingCalendar.pleaseWaitForCaseDetailToLoad'));
      return;
    }

    const appointmentOptions = shouldCreateAppointmentProposal ? buildAppointmentProposalOptions(data) : null;
    const meetingPayload = shouldCreateAppointmentProposal ? null : buildMeetingPayload(data);

    if (shouldCreateAppointmentProposal && !appointmentOptions) return;
    if (!shouldCreateAppointmentProposal && !meetingPayload) return;

    setIsCreatingMeeting(true);

    try {
      if (shouldCreateAppointmentProposal && appointmentOptions) {
        const response = await createPblAppointmentProposal(numericCaseId, {
          meeting_type: 'video',
          options: appointmentOptions,
        });

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToCreateAppointmentProposal'));
          return;
        }

        const message = t('pbl.pagesHardcoded.lawyerMeetingCalendar.pblMeetingCreated');
        successHandler(message);
        setPblCase((currentCase) =>
          currentCase
            ? {
                ...currentCase,
                status: 'pending_appointment_confirmation',
                appointment_proposals: [response.data.data, ...(currentCase.appointment_proposals || [])],
              }
            : currentCase
        );
        setHasSubmittedMeetingRequest(true);
        setMeetingOpen(false);
        setSuccessMessage(message);
        return;
      }

      if (!meetingPayload) return;

      const response = await createPblMeeting(numericCaseId, meetingPayload);

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToCreateMeeting'));
        return;
      }

      const meeting = response.data.data;
      const message = t('pbl.pagesHardcoded.lawyerMeetingCalendar.pblMeetingCreated');
      successHandler(message);
      setEvents((currentEvents) => [...currentEvents, mapMeetingToCalendarEvent(meeting)]);
      setPblCase((currentCase) =>
        currentCase
          ? {
              ...currentCase,
              meetings: [...(currentCase.meetings || []), meeting],
            }
          : currentCase
      );
      setHasSubmittedMeetingRequest(true);
      setMeetingOpen(false);
      setSuccessMessage(message);
    } catch (error: any) {
      errorHandler(
        error.response?.data?.message ||
          error.response?.data?.data ||
          (shouldCreateAppointmentProposal ? t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToCreateAppointmentProposal') : t('pbl.pagesHardcoded.lawyerMeetingCalendar.failedToCreateMeeting'))
      );
    } finally {
      setIsCreatingMeeting(false);
    }
  });

  const editProposedTimes = async (payload: PblAppointmentProposalUpdatePayload) => {
    if (!editingProposal || isCreatingMeeting || !payload.reason?.trim()) return;
    setIsCreatingMeeting(true);
    try {
      const response = await updatePblAppointmentProposal(editingProposal.id, payload);
      if (response.data.success === false) throw new Error(response.data.message);
      // Editing supersedes the old proposal ID. Reload server state instead of replacing an old array item.
      const refreshed = await getPblCase(Number(caseId));
      if (refreshed.data.success === false) throw new Error(refreshed.data.message);
      setPblCase(refreshed.data.data);
      setEditingProposal(null);
      successHandler(t('pbl.caseDetailDocuments.messages.proposedTimesUpdated'));
      navigate(buildCaseDetailPath(caseId!));
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.message || t('pbl.pagesHardcoded.caseProgressDetail.failedToUpdateProposedTimes'));
    } finally {
      setIsCreatingMeeting(false);
    }
  };

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={goBack}>
            {t('pbl.pagesHardcoded.lawyerMeetingCalendar.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.lawyerMeetingCalendar.home')}<strong>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.proBonoLegalServiceCasesReview')}</strong>
          </span>
        </TitleBar>
      }
    >
      <MeetingPageShell>
        <SummaryPanel isTablet={isTablet}>
          <SectionTitle>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.districtOfficeStaffInformation')}</SectionTitle>
          <InfoGrid>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.legcoMembersOffice')}</InfoLabel>
            <InfoValue>{caseSummary.officeName}</InfoValue>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.assistingStaffNameLegcoMembersOffice')}</InfoLabel>
            <InfoValue>{caseSummary.assistingStaffName}</InfoValue>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.officeContact')}</InfoLabel>
            <InfoValue>{caseSummary.officeContact}</InfoValue>
          </InfoGrid>

          <SectionTitle>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.caseInformation')}</SectionTitle>
          <InfoGrid>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.caseId')}</InfoLabel>
            <InfoValue>{caseSummary.caseId}</InfoValue>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.enquirer')}</InfoLabel>
            <InfoValue>{caseSummary.enquirer}</InfoValue>
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.hkid4Digits')}</InfoLabel>
            <InfoValue>{caseSummary.identityPartial}</InfoValue>
          </InfoGrid>
        </SummaryPanel>

        {(pblCase?.urgency === 'urgent' || pblCase?.court_deadline) && (
          <CaseDeadlineNotice role="note">
            {pblCase.urgency === 'urgent' && (
              <div>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.urgentCaseNotice')}</div>
            )}
            {pblCase.court_deadline && (
              <div>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.courtDeadlineNotice', {
                date: pblCase.court_deadline,
              })}</div>
            )}
            <div>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.deadlineSchedulingNotice')}</div>
          </CaseDeadlineNotice>
        )}

        <CalendarPanel isTablet={isTablet}>
          {editableProposal && (
            <Button type="button" variant="primary" size="md" disabled={isCreatingMeeting}
              onClick={() => setEditingProposal(editableProposal)}>
              {t('pbl.caseDetailDocuments.appointmentProposal.edit')}
            </Button>
          )}
          <CalendarScroller>
            <FullCalendar
              plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
              initialDate={formatDateForInput(minimumMeetingDateTime)}
              initialView="dayGridMonth"
              headerToolbar={{
                left: 'title',
                center: '',
                right: isMobile ? 'prev,today,next' : 'prev,today,next dayGridMonth,timeGridWeek',
              }}
              buttonText={{
                today: t('pbl.pagesHardcoded.lawyerMeetingCalendar.today'),
                month: t('pbl.pagesHardcoded.lawyerMeetingCalendar.month'),
                week: t('pbl.pagesHardcoded.lawyerMeetingCalendar.week'),
              }}
              titleFormat={(date) => `${date.date.year}年${date.date.month + 1}月`}
              dayHeaderContent={(header) => header.date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()}
              dayCellContent={(dayCell) => {
                const dateValue = formatDateForInput(dayCell.date);
                const isDisabled = isMeetingDateUnavailable(dayCell.date);

                return (
                  <button
                    type="button"
                    className="pbl-calendar-day-button"
                    aria-label={t('pbl.pagesHardcoded.lawyerMeetingCalendar.selectValue', { value1: dateValue })}
                    disabled={isDisabled}
                    onClick={(event) => {
                      event.stopPropagation();
                      openCreateMeetingModal(dayCell.date);
                    }}
                  >
                    {dayCell.dayNumberText}
                  </button>
                );
              }}
              dayCellClassNames={(dayCell) => {
                const classNames: string[] = [];

                if (formatDateForInput(dayCell.date) === selectedDate) {
                  classNames.push('pbl-selected-day');
                }

                if (isMeetingDateUnavailable(dayCell.date)) {
                  classNames.push('pbl-calendar-disabled-day');
                }

                return classNames;
              }}
              dateClick={(info) => openCreateMeetingModal(info.date)}
              events={calendarEvents}
              displayEventTime={false}
              eventClick={(eventInfo) => eventInfo.jsEvent.stopPropagation()}
              selectable={true}
              dayMaxEvents={true}
              height={isMobile ? 'auto' : '760px'}
              contentHeight="auto"
              fixedWeekCount={!isMobile}
              longPressDelay={1000}
              eventLongPressDelay={1000}
              selectLongPressDelay={1000}
              viewClassNames="pbl-lawyer-calendar"
            />
          </CalendarScroller>
        </CalendarPanel>
      </MeetingPageShell>

      <AppointmentProposalEditModal showModal={Boolean(editingProposal)}
        setShowModal={(show) => { if (!show && !isCreatingMeeting) setEditingProposal(null); }}
        proposal={editingProposal} isSubmitting={isCreatingMeeting} onSubmit={editProposedTimes} reasonRequired />
      <Modal open={meetingOpen} onClose={closeCreateMeetingModal} aria-labelledby="create-pbl-meeting-title">
        <MeetingModalSurface
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-pbl-meeting-title"
          onSubmit={createMeeting}
        >
          <MeetingModalCloseButton
            type="button"
            aria-label={t('pbl.pagesHardcoded.lawyerMeetingCalendar.closeCreateMeetingModal')}
            onClick={closeCreateMeetingModal}
          >
            x
          </MeetingModalCloseButton>
          <MeetingModalTitle id="create-pbl-meeting-title">{t('pbl.pagesHardcoded.lawyerMeetingCalendar.createMeeting')}</MeetingModalTitle>
          <MeetingFieldStack>
            {displayedOptions.map((option, index) => {
              const optionErrors = errors.options?.[index];
              const optionNumber = index + 1;
              const dateLabel = shouldCreateAppointmentProposal ? t('pbl.pagesHardcoded.lawyerMeetingCalendar.optionValueMeetingDate', { value1: optionNumber }) : t('pbl.pagesHardcoded.lawyerMeetingCalendar.meetingDate');
              const startTimeLabel = shouldCreateAppointmentProposal ? t('pbl.pagesHardcoded.lawyerMeetingCalendar.optionValueStartTime', { value1: optionNumber }) : t('pbl.pagesHardcoded.lawyerMeetingCalendar.startTime');
              const endTimeLabel = shouldCreateAppointmentProposal ? t('pbl.pagesHardcoded.lawyerMeetingCalendar.optionValueEndTime', { value1: optionNumber }) : t('pbl.pagesHardcoded.lawyerMeetingCalendar.endTime');
              const endsAt = getPblMeetingEndDateTime(`${option.date}T${option.startTime}`);
              const endTimeDisplay = endsAt
                ? endsAt.slice(0, 10) === option.date ? endsAt.slice(11) : endsAt.replace('T', ' ')
                : '-';

              return (
                <MeetingOptionPanel key={index}>
                  {shouldCreateAppointmentProposal && (
                    <MeetingOptionHeader>
                      <span>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.option')}{optionNumber}</span>
                      {watchedOptions.length > MINIMUM_APPOINTMENT_OPTIONS && (
                        <MeetingSecondaryButton type="button" onClick={() => removeAppointmentOption(index)}>
                          {t('pbl.pagesHardcoded.lawyerMeetingCalendar.remove')}</MeetingSecondaryButton>
                      )}
                    </MeetingOptionHeader>
                  )}
                  <MeetingFieldRow>
                    <Controller
                      control={control}
                      name={`options.${index}.date` as const}
                      rules={{ required: t('pbl.pagesHardcoded.lawyerMeetingCalendar.meetingDateIsRequired') }}
                      render={({ field }) => (
                        <MeetingInputWrap>
                          <HiddenLabelText as="span">{dateLabel}</HiddenLabelText>
                          <MeetingInput
                            aria-label={dateLabel}
                            aria-invalid={!!optionErrors?.date}
                            autoComplete="off"
                            min={minimumDateInput}
                            type="date"
                            {...field}
                          />
                          {optionErrors?.date?.message && (
                            <MeetingValidationText>{optionErrors.date.message}</MeetingValidationText>
                          )}
                        </MeetingInputWrap>
                      )}
                    />
                    <Controller
                      control={control}
                      name={`options.${index}.startTime` as const}
                      rules={{ required: t('pbl.pagesHardcoded.lawyerMeetingCalendar.startTimeIsRequired') }}
                      render={({ field }) => (
                        <MeetingInputWrap>
                          <HiddenLabelText as="span">{startTimeLabel}</HiddenLabelText>
                          <MeetingInput
                            aria-label={startTimeLabel}
                            aria-invalid={!!optionErrors?.startTime}
                            autoComplete="off"
                            min={getMinimumTimeInputForDate(option?.date)}
                            step={300}
                            type="time"
                            {...field}
                          />
                          {optionErrors?.startTime?.message && (
                            <MeetingValidationText>{optionErrors.startTime.message}</MeetingValidationText>
                          )}
                        </MeetingInputWrap>
                      )}
                    />
                    <MeetingInputWrap as="div">
                      <MeetingEndOutput aria-label={endTimeLabel}>{endTimeDisplay}</MeetingEndOutput>
                    </MeetingInputWrap>
                  </MeetingFieldRow>
                </MeetingOptionPanel>
              );
            })}

            {shouldCreateAppointmentProposal && watchedOptions.length < MAXIMUM_APPOINTMENT_OPTIONS && (
              <MeetingOptionActionRow>
                <MeetingSecondaryButton type="button" onClick={addAppointmentOption}>
                  {t('pbl.pagesHardcoded.lawyerMeetingCalendar.addOption')}</MeetingSecondaryButton>
              </MeetingOptionActionRow>
            )}

            <Controller
              control={control}
              name="description"
              render={({ field }) => (
                <MeetingInputWrap>
                  <MeetingDescriptionInput
                    placeholder={t('pbl.pagesHardcoded.lawyerMeetingCalendar.description')}
                    aria-label={t('pbl.pagesHardcoded.lawyerMeetingCalendar.description')}
                    autoComplete="off"
                    {...field}
                  />
                </MeetingInputWrap>
              )}
            />
          </MeetingFieldStack>

          <MeetingModalAction>
            <MeetingActionButton
              type="submit"
              size="lg"
              variant="primary"
              rounded={false}
              disabled={isCreatingMeeting || hasSubmittedMeetingRequest}
            >
              {isCreatingMeeting
                ? t('pbl.pagesHardcoded.lawyerMeetingCalendar.creating')
                : t('pbl.common.actions.ok', { defaultValue: 'OK' })}
            </MeetingActionButton>
            <MeetingActionButton
              type="button"
              size="lg"
              variant="primary-invert"
              rounded={false}
              onClick={closeCreateMeetingModal}
            >
              {t('pbl.pagesHardcoded.lawyerMeetingCalendar.cancel')}</MeetingActionButton>
          </MeetingModalAction>
        </MeetingModalSurface>
      </Modal>

      <Modal open={Boolean(successMessage)} onClose={goBack} aria-labelledby="pbl-meeting-created-title">
        <MeetingModalSurface role="dialog" aria-modal="true" aria-labelledby="pbl-meeting-created-title">
          <MeetingModalTitle id="pbl-meeting-created-title">{t('pbl.pagesHardcoded.lawyerMeetingCalendar.meetingCreatedSuccessfully')}</MeetingModalTitle>
          <MeetingSuccessTitle>{t('pbl.pagesHardcoded.lawyerMeetingCalendar.meetingCreatedSuccessfully')}</MeetingSuccessTitle>
          <MeetingSuccessText>{successMessage}</MeetingSuccessText>
          <MeetingModalAction>
            <MeetingActionButton type="button" size="lg" variant="primary" rounded={false} onClick={goBack}>
              {t('pbl.common.actions.ok', { defaultValue: 'OK' })}
            </MeetingActionButton>
          </MeetingModalAction>
        </MeetingModalSurface>
      </Modal>
    </PageContainer>
  );
};

export default PblLawyerMeetingCalendar;
